import React, { useState } from 'react';
import { Box, ButtonBase, Tooltip } from '@material-ui/core';
import { VariantType } from 'notistack';
import { ConfirmDialog, CustomIcon, QuantityWarningDialog } from '../../../custom-forms';
import { useStyles, cx } from '../../../custom-forms';
import { CaseCalculationPart } from '../../../interfaces/Case';
import { IReferential } from '../../../interfaces/IReferential';
import { WorkflowCalculation } from '../../../interfaces/Workflow';
import { useTranslate } from '../../../hooks/useTranslate';
import { useVariantsEnabled } from '../context/CalculationFlagsContext';
import { CalculationActions } from '../types/alternative-calculation.types';
import { GridCellCtx, GridColumnConfig } from '../config/columns';
import CalculationNotifySupportDialog from './CalculationNotifySupportDialog';

const QUANTITY_WARNING_THRESHOLD = 10;

export interface AlternativeCalculationPartHeaderProps {
  part: CaseCalculationPart;
  workflowCalculation: WorkflowCalculation | null;
  qualities: IReferential[];
  actions: CalculationActions;
  onNotify: (message: string, variant: VariantType) => void;
  onCopyPart: (part: CaseCalculationPart) => void;
  onAddAlternativePart: () => void;
  // Clears the active filter for this line only (until the next filter re-apply).
  onResetFilter: () => void;
  canResetFilter: boolean;
  infoTooltip: React.ReactNode | null;
  columns: GridColumnConfig[];
  canExpand: boolean;
}

const stop = (e: React.SyntheticEvent) => e.stopPropagation();

const AlternativeCalculationPartHeader: React.FC<AlternativeCalculationPartHeaderProps> = ({
  part,
  qualities,
  actions,
  onNotify,
  onAddAlternativePart,
  onResetFilter,
  canResetFilter,
  infoTooltip,
  columns,
  canExpand
}) => {
  const c = useStyles();
  const t = useTranslate();
  const variantsEnabled = useVariantsEnabled();
  const hasSelection = part.alternativeParts.some((a) => a.isVariantSelected);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const confirmDeletePart = () => {
    actions.deletePart(part.id);
    onNotify(
      `„${part.name}" ${t('ALT_CALC_PART_DELETED', 'a fost ștearsă din calculație')}`,
      'success'
    );
    setDeleteConfirmOpen(false);
  };
  const toggle = () => actions.toggleExpand(part.id);

  // Click on any column toggles, except editable inputs/selects/labels and buttons.
  const isEditableTarget = (target: EventTarget | null) =>
    target instanceof HTMLElement && target.closest('input, select, label, button') != null;

  const handleRowClick = (e: React.MouseEvent<HTMLElement>) => {
    if (canExpand && !isEditableTarget(e.target)) {
      toggle();
    }
  };

  const handleRowKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (canExpand && (e.key === 'Enter' || e.key === ' ') && !isEditableTarget(e.target)) {
      e.preventDefault();
      toggle();
    }
  };

  const [notifyOpen, setNotifyOpen] = useState(false);
  const [isSendingSupport, setIsSendingSupport] = useState(false);
  const [pendingQty, setPendingQty] = useState<number | null>(null);

  const applyPieceNr = (value: number) => actions.updatePieceNr(part.id, value);

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = isNaN(parseFloat(e.target.value)) ? 0 : parseFloat(e.target.value);
    if (newValue >= QUANTITY_WARNING_THRESHOLD) {
      setPendingQty(newValue);
      return;
    }
    applyPieceNr(newValue);
  };

  const confirmQty = () => {
    if (pendingQty !== null) {
      applyPieceNr(pendingQty);
    }
    setPendingQty(null);
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    actions.updateOriginalPrice(part.id, e.target.value === '' ? 0 : parseFloat(e.target.value));

  const handleSendNotificationToSupport = async (payload: CaseCalculationPart) => {
    setIsSendingSupport(true);
    try {
      await actions.sendNotificationToSupport(payload);
      setNotifyOpen(false);
    } finally {
      setIsSendingSupport(false);
    }
  };

  const cellCtx: GridCellCtx = {
    part,
    alternativePart: null,
    onPriceChange: handlePriceChange,
    onQuantityChange: handleQuantityChange,
    onQuery: () => actions.interogateAlternativeParts(part),
    onOpenNotify: () => setNotifyOpen(true),
    onAddAlternativePart,
    onDeletePart: () => setDeleteConfirmOpen(true),
    infoTooltip
  };

  return (
    <>
      <Box
        className={cx(c.gridRow, c.groupHead, c.gutterReserve)}
        role="button"
        tabIndex={0}
        onClick={handleRowClick}
        onKeyDown={handleRowKeyDown}
      >
        <Box>
          <ButtonBase
            className={cx(c.chev, !canExpand && c.hidden)}
            component="span"
            disabled={!canExpand}
          >
            <CustomIcon name="chevron" />
          </ButtonBase>
        </Box>
        <Box className={cx(c.row, c.gap8)}>
          <Tooltip
            title={
              variantsEnabled
                ? t('ALT_CALC_UNSELECT_PART', 'Deselectează variantele acestei piese')
                : t('ALT_CALC_UNSELECT_PART_SELECTIONS', 'Deselectează selecția acestei piese')
            }
            placement="left"
          >
            <ButtonBase
              className={cx(c.iconBtn, c.iconBtnSm, !hasSelection && c.hidden)}
              onClick={(e) => {
                stop(e);
                actions.unselectPart(part.id);
              }}
            >
              <CustomIcon name="ban" />
            </ButtonBase>
          </Tooltip>
          {canResetFilter && (
            <Tooltip
              title={t('ALT_CALC_RESET_LINE_FILTER', 'Resetează filtrele pentru această piesă')}
              placement="left"
            >
              <ButtonBase
                className={cx(c.iconBtn, c.iconBtnSm)}
                onClick={(e) => {
                  stop(e);
                  onResetFilter();
                }}
              >
                <CustomIcon name="filterReset" />
              </ButtonBase>
            </Tooltip>
          )}
        </Box>
        {columns.map((col) => (
          <React.Fragment key={col.key}>{col.renderBodyCell(cellCtx)}</React.Fragment>
        ))}
      </Box>

      <CalculationNotifySupportDialog
        open={notifyOpen}
        part={part}
        qualities={qualities}
        isLoading={isSendingSupport}
        onClose={() => setNotifyOpen(false)}
        onSend={handleSendNotificationToSupport}
      />

      <QuantityWarningDialog
        open={pendingQty !== null}
        title={t('QUANTITY_WARNING_DIALOG_TITLE', 'Cantitate mare')}
        message={t(
          'QUANTITY_WARNING_DIALOG_MESSAGE',
          'Cantitatea introdusă este mai mare sau egală cu 10. Doriți să continuați?'
        )}
        confirmLabel={t('WORKFLOW_DOCUMENTS_TYPE_TAB_YES', 'Da')}
        cancelLabel={t('WORKFLOW_DOCUMENTS_TYPE_TAB_NO', 'Nu')}
        info={
          pendingQty !== null
            ? { label: t('QUANTITY_WARNING_DIALOG_QUANTITY_LABEL', 'Cantitate'), value: pendingQty }
            : null
        }
        onConfirm={confirmQty}
        onClose={() => setPendingQty(null)}
      />

      <ConfirmDialog
        open={deleteConfirmOpen}
        title={t('ALT_CALC_DELETE_PART', 'Șterge piesa din calculație')}
        message={t('ALT_CALC_DELETE_PART_CONFIRM', 'Sunteți sigur că doriți să ștergeți piesa?')}
        confirmLabel={t('WORKFLOW_DOCUMENTS_TYPE_TAB_YES', 'Da')}
        cancelLabel={t('WORKFLOW_DOCUMENTS_TYPE_TAB_NO', 'Nu')}
        onConfirm={confirmDeletePart}
        onClose={() => setDeleteConfirmOpen(false)}
      />
    </>
  );
};

export default React.memo(AlternativeCalculationPartHeader);
